import React, { useEffect, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import LoadingState from '../common/LoadingState';
import EmptyState from '../common/EmptyState';
import { fmtDateTime, fmtAgo, errMsg, cachedGet } from './shared';

// "What has this allocation been doing?" - backend utils/activity/bids.js
// allocationActivity: its settings, and every loan it recently evaluated.

const OUTCOME = {
    placed: { label: 'Bid placed', variant: 'ex-status-success' },
    undetermined: { label: 'Undetermined', variant: 'ex-status-info' },
    failed: { label: 'Bid failed', variant: 'ex-status-danger' },
    skipped: { label: 'Not bid', variant: 'ex-status-warning' },
};
const LOAN_TYPE = { SME: 'SME', CONSUMER: 'Consumer', INVOICE_FINANCING: 'Invoice financing' };
const fmtNum = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 }));

const AllocationLookup = ({ platform, onOpenLoan }) => {
    const [options, setOptions] = useState(null);
    const [selected, setSelected] = useState('');
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setOptions(null);
        cachedGet(`/activity/platforms/${encodeURIComponent(platform)}/allocations`)
            .then((res) => setOptions(res.data.allocations.map((a) => ({
                value: a.key,
                label: `${a.name}${a.loan_type ? ` · ${LOAN_TYPE[String(a.loan_type).toUpperCase()] || a.loan_type}` : ''}${a.active ? '' : ' · off'} (${a.key})`,
            }))))
            .catch(() => setOptions([]));
    }, [platform]);

    const show = (key, force = false) => {
        const k = String(key || selected || '').trim();
        if (!k) return;
        setLoading(true);
        setError(null);
        setResult(null);
        cachedGet(`/activity/platforms/${encodeURIComponent(platform)}/allocations/${encodeURIComponent(k)}`, { params: { days: 7 }, force })
            .then((res) => setResult(res.data))
            .catch((err) => setError(errMsg(err)))
            .finally(() => setLoading(false));
    };

    const a = result?.allocation;

    return (
        <div>
            <form className="ex-toolbar" onSubmit={(e) => { e.preventDefault(); show(); }}>
                <div className="ex-toolbar-group">
                    <Dropdown value={selected} options={options || []} editable filter showClear
                        placeholder={options && !options.length ? 'Type an allocation ID' : 'Pick an allocation or type its ID'}
                        onChange={(e) => { setSelected(e.value || ''); if (options?.some((o) => o.value === e.value)) show(e.value); }}
                        style={{ minWidth: '24rem' }} />
                    <Button type="submit" label="Show allocation" icon="pi pi-search" loading={loading} disabled={!String(selected).trim()} />
                </div>
            </form>

            {error && <div className="ex-inline-error">{error}</div>}
            {loading && <LoadingState label="Loading this allocation… (Savy reads its bidder logs, a few seconds)" size="sm" />}
            {result && !a && <EmptyState compact icon="pi-search" title="Allocation not found" message="Check the allocation ID - it is not in LoanSweeper." />}

            {a && (
                <div className="act-loan">
                    <div className="act-explain act-verdict-placed">
                        <div className="act-explain-head">
                            <strong>{a.name}</strong>
                            <span className={`ex-status ${a.active ? 'ex-status-success' : 'ex-status-danger'}`}>{a.active ? 'Active' : 'Switched off'}</span>
                            {a.loan_type && <span className="ex-status">{LOAN_TYPE[String(a.loan_type).toUpperCase()] || a.loan_type}</span>}
                            {a.currency && <span className="ex-status">{a.currency}</span>}
                            <small className="text-color-secondary">ID {a.key}</small>
                            <Button icon="pi pi-refresh" className="p-button-text p-button-sm" tooltip="Refresh (fresh data)" onClick={() => show(a.key, true)} />
                        </div>
                        {!a.on_this_platform && <div>This allocation does not list {platform} as an Originator ({a.platforms.join(', ') || 'none'}).</div>}
                        {a.criteria.length > 0 && (
                            <dl className="ex-kv act-criteria">
                                {a.criteria.map((c) => <React.Fragment key={c.label}><dt>{c.label}</dt><dd>{c.value}</dd></React.Fragment>)}
                            </dl>
                        )}
                    </div>

                    <div className="act-received">
                        <div className="act-received-tile"><div className="act-received-type">Bids placed</div><div>{result.counts.placed}</div></div>
                        {result.counts.undetermined > 0 && (
                            <div className="act-received-tile"><div className="act-received-type">Outcome undetermined</div><div>{result.counts.undetermined}</div></div>
                        )}
                        <div className="act-received-tile"><div className="act-received-type">Bids failed</div><div>{result.counts.failed}</div></div>
                        <div className="act-received-tile"><div className="act-received-type">Loans not bid</div><div>{result.counts.skipped}</div></div>
                    </div>

                    {result.top_skip_reasons.length > 0 && (
                        <div>
                            <div className="act-subtitle">Most common reasons for not bidding</div>
                            <ul className="act-reasons">
                                {result.top_skip_reasons.map((r) => <li key={r.reason}>{r.reason} <span className="text-color-secondary">({r.n} loan{r.n === 1 ? '' : 's'})</span></li>)}
                            </ul>
                        </div>
                    )}

                    <div className="act-subtitle">Loans it evaluated</div>
                    {result.decisions.length === 0
                        ? <EmptyState compact icon="pi-inbox" title="No bidding decisions found" message={result.note} />
                        : (
                            <DataTable value={result.decisions} dataKey="loan_id" size="small" responsiveLayout="scroll" paginator rows={15}
                                selectionMode="single" onRowSelect={(e) => onOpenLoan && onOpenLoan(e.data.loan_id)} rowClassName={() => 'act-clickable'}>
                                <Column field="loan_id" header="Loan ID" />
                                <Column header="When" body={(d) => (d.at ? <span title={fmtDateTime(d.at)}>{fmtAgo(d.at)}</span> : '—')} />
                                <Column header="Result" body={(d) => <span className={`ex-status ${OUTCOME[d.outcome]?.variant || ''}`}>{OUTCOME[d.outcome]?.label || d.outcome}</span>} />
                                <Column header="Amount" body={(d) => fmtNum(d.amount)} />
                                <Column header="Why" body={(d) => d.reason || '—'} />
                            </DataTable>
                        )}
                    {result.decisions.length > 0 && result.note && <small className="text-color-secondary">{result.note} Click a loan to see all allocations for it.</small>}
                    {result.source_error && <div className="ex-inline-error">Bid decisions could not be loaded: {result.source_error}</div>}
                </div>
            )}
        </div>
    );
};

export default AllocationLookup;
